import { CommunicationContent } from "@/components/communications/CommunicationContent.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { Card } from "@/components/ui/card.tsx";
import type { CommunicationDetail } from "@/types/communications.ts";
import { useCommunication } from "@/hooks/useCommunication.ts";

/**
 * The prop contract a canvas/sidebar entity renderer satisfies. `density` is a
 * host hint — the sidebar is narrow (`compact`), the canvas wide (`comfortable`);
 * renderers may adjust to it or ignore it.
 */
export type EntityViewProps = {
  id: string;
  density?: "compact" | "comfortable";
};

/** The host frame's title for a communication — "Message" for texts, "Email" otherwise. */
export const communicationTitle = (channel: CommunicationDetail["channel"] | undefined): string =>
  channel === "text" ? "Message" : "Email";

/**
 * Read-only view of a single email / text message. Mounts unchanged in either
 * host — the inspector sidebar or the thread canvas — which each wrap it with
 * their own frame (header, close affordance, container). The sidebar passes only
 * an `id` and the view fetches; the canvas already resolved the detail (via
 * `/api/a/sources/:id`) and passes it as `data`, so the fetch is skipped.
 */
export const CommunicationView = ({
  id,
  data,
}: EntityViewProps & { data?: CommunicationDetail }) => {
  const { communication, isLoading } = useCommunication({ id: data ? undefined : id });
  const detail = data ?? communication;

  if (!detail) {
    if (isLoading) return <Loader size="sm" />;
    return <p className="text-muted-foreground text-sm">Message not found.</p>;
  }
  return (
    <Card className="p-0 shadow-none">
      {/* Both hosts show one message on its own, so the agent composer is
          unambiguous about which message "this" means. */}
      <CommunicationContent data={detail} showAgentComposer />
    </Card>
  );
};

/**
 * Header title for whichever host wraps {@link CommunicationView}. Shares the
 * `useCommunication` query with the view (React Query dedupes), so the header
 * reflects the loaded channel.
 */
export const useCommunicationHeader = (id: string): { title: string } => {
  const { communication } = useCommunication({ id });
  return { title: communicationTitle(communication?.channel) };
};
